<i18n>
{
	"en": {
		"itemsFound": "{count} found",
		"placeholder": "Filter and choose"
	},
	"no": {
		"itemsFound": "{count} treff",
		"placeholder": "Filtrer og velg"
	}
}
</i18n>

<template>
	<t-popover
		:showPopover="shouldShowPopover"
		position="bottom"
		:min-width="150"
		relative
	>
		<t-search-input
			slot="target"
			ref="input"
			:value="searchValue"
			:placeholder="placeholder || $t('placeholder')"
			v-bind="$attrs"
			@input="onInput"
			@focus="onFocus"
			@blur="onInputBlur"
			@keydown.tab="onTab"
			@keydown.enter="onEnter"
		/>

		<t-menu v-if="filteredItems">
			<t-menu-options-group
				v-slot="slotProps"
				:title="$t('itemsFound', { count: itemsFoundTitle })"
				:items="filteredItems"
				:selectedItems="selectedItems"
				groupStyle="combobox"
				disableSpaceToSelect
				@update:selected="onSelected"
				@mouseenter="onTMenuMouseenter"
				@mouseleave="onTMenuMouseleave"
			>
				<slot :item="slotProps.item" :isSelected="slotProps.isSelected">
					<t-menu-combobox-option-item
						:value="slotProps.item"
						:selected="slotProps.isSelected"
					>
						<div class="list-item">
							<div class="list-item-left">
								<div class="list-item-main-text">{{ slotProps.item | objGet(primaryKey) }}</div>
								<div class="list-item-sub-text">{{ slotProps.item | objGet(secondaryKey) }}</div>
							</div>
							<div v-if="tertiaryKey" class="list-item-right">
								{{ slotProps.item | objGet(tertiaryKey)}}
							</div>
						</div>
					</t-menu-combobox-option-item>
				</slot>
			</t-menu-options-group>
		</t-menu>
	</t-popover>
</template>

<script>
import popoverMixin from '@/components/evergreen/inputs/popoverMixin';
import { objectGet } from '@/core';

export default {
	SERVER_RETURN_LIMIT: 50,
	name: 'TMultiselect',

	filters: {
		objGet(item, key) {
			return objectGet(item, key);
		},
	},

	mixins: [popoverMixin],
	inheritAttrs: false,
	props: {
		allItems: Array,
		selectedItems: Array,
		itemKeys: Array,
		placeholder: String,
		closeOnSelect: Boolean,
	},

	data() {
		return {
			searchValue: '',
			lastSearch: null,
			mouseIsOverTMenu: false,
			searchTimeout: null,
		};
	},

	computed: {
		shouldShowPopover() {
			return this.popoverIsVisible && this.searchValue.length > 0 && this.filteredItems.length > 0;
		},
		filteredItems() {
			if (!this.searchValue) return this.allItems;
			let result = this.allItems.filter(item => this.itemsFilter(item));
			return result;
		},
		computedValidationMessage() {
			return this.errors.reduce((acc, v) => acc || v, false) || '';
		},

		primaryKey() {
			return this.itemKeys && this.itemKeys.length && this.itemKeys[0];
		},
		secondaryKey() {
			return this.itemKeys && this.itemKeys.length && this.itemKeys[1];
		},
		tertiaryKey() {
			return this.itemKeys && this.itemKeys.length && this.itemKeys[2];
		},

		itemsFoundTitle() {
			return this.$options.SERVER_RETURN_LIMIT &&
				this.filteredItems.length >= this.$options.SERVER_RETURN_LIMIT ?
				`${this.$options.SERVER_RETURN_LIMIT}+` :
				this.filteredItems.length;
		},
	},

	methods: {
		itemsFilter(item) {
			if (!item) return false;
			let ret = true;
			let searchString = this.searchValue.toLowerCase();

			for (let searchWord of searchString.split(' ')) {
				ret = ret &&
						this.itemKeys.reduce((hasMatch, filterKey) =>
							(hasMatch || (objectGet(item, filterKey) &&
								`${objectGet(item, filterKey)}`.toLowerCase().includes(searchWord))),
						false);
			}
			return ret;
		},

		maybeUpdateFilters() {
			if (this.searchValue === '') return;
			if (this.allItems.length < this.$options.SERVER_RETURN_LIMIT && this.searchValue.startsWith(this.lastSearch)) return;
			this.lastSearch = this.searchValue;
			this.eventuallyUpdateFilters();
		},

		eventuallyUpdateFilters() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.$emit('updateFilters', this.searchValue);
			},300);
		},

		clearInput() {
			this.onInput('');
		},

		onInput(value) {
			this.searchValue = value;
			this.maybeUpdateFilters();
			this.$emit('input', value);
		},

		onSelected(item) {
			if (item === null) return;
			if (this.itemIsAllreadySelected(item)) {
				if (Object.keys(this.$listeners).includes('set:selected')) {
					this.$emit('set:selected', this.getNewSelectedItems(item));
				}
				if (Object.keys(this.$listeners).includes('remove')) {
					this.$emit('remove', this.getToRemoveIndex(item));
				}
				return;
			}
			this.$emit('select', item);
			if (this.closeOnSelect) this.clearInput();
		},

		itemIsAllreadySelected(item) {
			if (!this.itemKeys || !this.itemKeys.length) {
				return this.selectedItems.includes(item);
			}

			let itemValues = this.itemKeys.map(k => objectGet(item, k));
			let storedValues = this.itemKeys.map(
				k => this.selectedItems.map(
					selectedItem => objectGet(selectedItem, k)
				)
			);

			return itemValues.reduce(
				(acc, v, i) => storedValues[i].includes(v) && acc, true
			);
		},

		getNewSelectedItems(itemToRemove) {
			if (!this.itemKeys || !this.itemKeys.length) {
				return this.selectedItems.filter(
					selectedItem => selectedItem !== itemToRemove
				);
			}

			let itemIndex = this.selectedItems.map(
				selectedItem => objectGet(selectedItem, this.itemKeys[0])
			).indexOf(objectGet(itemToRemove, this.itemKeys[0]));

			let newSelectedItems = [...this.selectedItems];
			newSelectedItems.splice(itemIndex, 1);
			return newSelectedItems;
		},

		getToRemoveIndex(itemToRemove) {
			if (!this.itemKeys || !this.itemKeys.length) {
				return this.selectedItems.indexOf(itemToRemove);
			}
			return this.selectedItems.map(
				selectedItem => objectGet(selectedItem, this.itemKeys[0])
			).indexOf(objectGet(itemToRemove, this.itemKeys[0]));
		},

		onInputBlur() {
			if (this.mouseIsOverTMenu) {
				if (!this.popoverIsVisible) {
					// This happens if pressing escape when hovering the popover
					this.mouseIsOverTMenu = false;
					return;
				}
				// This happens when the user is selecting items using the mouse.
				// Just reset the focus to keep the popup open.
				this.$refs.input.focus();
				return;
			}
			this.popoverSetHidden();
		},

		onTMenuMouseenter() {
			this.mouseIsOverTMenu = true;
		},
		onTMenuMouseleave() {
			this.mouseIsOverTMenu = false;
		},

		onTab() {
			// Allow focusing the next element
			this.mouseIsOverTMenu = false;
		},
		onEnter(event) {
			// QUESTION: What behaviour do we actually want here?
			if (this.searchValue) {
				this.$emit('change', this.searchValue);
				this.clearInput();
			} else {
				this.$emit('enter', event);
			}
			this.onTab();
		},

		onFocus(event) {
			this.popoverSetVisible();
			this.$emit('focus', event);
		},
	},
};
</script>

<style lang="scss" scoped>
.list-item {
	display: grid;
	grid-template-columns: min-content 1fr;
	height: 100%;
	width: 100%;
}

.list-item-left {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.list-item-right {
	justify-self: end;
	margin-right: 10px;
	height: 100%;
	font-size: 12px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.list-item-main-text {
	height: 16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.list-item-sub-text {
	height: 16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}
</style>
